import React, { useState, useEffect } from "react";
import "../Tabs.css";
import ShoppingAdDetail from "./components/ShoppingAdDetail";
import NormalAdDetail from "./components/NormalAdDetail";
import CoupangAdDetail from "./components/CoupangAdDetail";
import PlaceAdDetail from "./components/PlaceAdDetail";
import AppReviewAdDetail from "./components/AppReviewAdDetail";
import { useAuth } from "../../../../hooks/useAuth";
import {
  getMissionServiceAPI,
  getMissionActionAPI,
  getMemberAdAPI,
  getKeywordNameAPI,
  getPriceGradeDetailAPI,
  getUserDetailAPI,
} from "../../../../api/index";
import { convertUTCToKoreaTime } from "../../../../utils/helpers";

// 테이블 컴포넌트 import
import ShoppingAdTable from "./components/tables/ShoppingAdTable";
import ShoppingOriginAdTable from "./components/tables/ShoppingOriginAdTable";
import PlaceAdTable from "./components/tables/PlaceAdTable";
import ReviewAdTable from "./components/tables/ReviewAdTable";
import AdExtensionModal from "./components/AdExtensionModal";
import RankCheckModal from "./components/RankCheckModal";
import QuantityIncreaseHistoryModal from "./components/QuantityIncreaseHistoryModal";
import AdEndHistoryModal from "./components/AdEndHistoryModal";
import MyAdvertisementMobile from "./MyAdvertisementMobile";
import "./MyAdvertisement.css";

const AD_PAGE_SIZE = 100;

const MyAdvertisement = () => {
  const { user } = useAuth();

  // 모바일 감지
  const [isMobile, setIsMobile] = useState(false);

  // 모든 hooks를 early return 전에 호출해야 함
  const [selectedMainTab, setSelectedMainTab] = useState("naver");
  const [selectedSubTab, setSelectedSubTab] = useState("shopping");

  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("missionName"); // 검색 타입: missionName 또는 storeName
  // ✅ 상태를 부모에서만 관리
  const [selectedAds, setSelectedAds] = useState([]);
  const [statusFilter, setStatusFilter] = useState(() => {
    // 등록 완료 → 「내 광고 확인」으로 진입 시에만 PENDING
    const params = new URLSearchParams(window.location.search);
    return params.get("status") === "PENDING" ? "PENDING" : "APPROVED";
  }); // 기본값: 진행중
  const [isUrgent, setIsUrgent] = useState(false); // 마감임박 체크박스 상태
  const [selectedAdData, setSelectedAdData] = useState(null); // 상세보기용 선택된 광고 데이터
  const [adSummary, setAdSummary] = useState({
    total: 0,
    active: 0,
    pending: 0,
    ended: 0,
  });

  // 광고 일괄 연장 모달 상태 추가
  const [isBulkExtensionModalOpen, setIsBulkExtensionModalOpen] =
    useState(false);

  // 순위체크 모달 상태 추가
  const [isRankCheckModalOpen, setIsRankCheckModalOpen] = useState(false);
  const [selectedAdForRankCheck, setSelectedAdForRankCheck] = useState(null);

  // 수량증가 내역 모달 상태
  const [isQuantityIncreaseHistoryOpen, setIsQuantityIncreaseHistoryOpen] =
    useState(false);
  // 광고 종료 신청 내역 모달 상태
  const [isAdEndHistoryOpen, setIsAdEndHistoryOpen] = useState(false);

  // API 데이터 상태
  const [missionServices, setMissionServices] = useState([]);
  const [missionActions, setMissionActions] = useState([]);
  const [adData, setAdData] = useState([]); // 광고 데이터
  const [keywordData, setKeywordData] = useState({}); // 키워드 데이터
  const [priceGradeData, setPriceGradeData] = useState(null); // 가격 등급 데이터
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 페이징 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [pagination, setPagination] = useState(null);

  // 로그인되지 않은 경우 목데이터 (useEffect에서 사용하므로 hooks 전에 정의)
  const mockSubTabs = {
    naver: [
      {
        id: "shopping",
        label: "쇼핑 방문",
        active: selectedSubTab === "shopping",
      },
      {
        id: "shopping_origin",
        label: "쇼핑(원부) 방문",
        active: selectedSubTab === "shopping_origin",
      },
      {
        id: "place",
        label: "플레이스 방문",
        active: selectedSubTab === "place",
      },
      { id: "save", label: "플레이스 저장", active: selectedSubTab === "save" },
    ],
    review: [{ id: "app", label: "앱 리뷰", active: selectedSubTab === "app" }],
  };

  // 모바일 감지 useEffect
  useEffect(() => {
    // 초기 모바일 감지 (한 번만, 더 정확한 방법 사용)
    const initialWidth = window.visualViewport?.width || window.innerWidth;
    const initialIsMobile = initialWidth <= 768;
    setIsMobile(initialIsMobile);
    
    // 모바일로 감지되면 리사이즈 이벤트 무시 (옛날 디자인으로 넘어가는 것 방지)
    if (initialIsMobile) {
      return;
    }
    
    // 데스크톱인 경우에만 리사이즈 이벤트 처리
    const checkMobile = () => {
      const width = window.visualViewport?.width || window.innerWidth;
      setIsMobile(width <= 768);
    };

    // 디바운싱하여 너무 자주 발생하지 않도록
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(checkMobile, 100);
    };
    
    window.addEventListener("resize", handleResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleResize);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", handleResize);
      }
      clearTimeout(resizeTimeout);
    };
  }, []);

  // 컴포넌트 마운트 시 미션 서비스 로딩
  useEffect(() => {
    if (user?.id) {
      loadMissionServices();
      loadAdSummaryCounts();
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // 로그아웃 상태에서 메인 탭 변경 시 첫 번째 서브 탭 자동 선택
  useEffect(() => {
    if (!user?.id && selectedMainTab) {
      const availableSubTabs = mockSubTabs[selectedMainTab];

      if (availableSubTabs && availableSubTabs.length > 0) {
        const firstSubTab = availableSubTabs[0];
        setSelectedSubTab(firstSubTab.id);
      }
    }
  }, [selectedMainTab, user?.id]);

  // statusFilter 또는 isUrgent 변경 시 1페이지부터 다시 로드
  useEffect(() => {
    if (user?.id && selectedMainTab && selectedSubTab) {
      const currentService = missionServices.find(
        (s) => s.id === selectedMainTab
      ) || { ms_id: 1 };
      const currentAction = missionActions.find(
        (a) => a.id === selectedSubTab
      ) || { ma_id: 1 };

      setCurrentPage(1);
      loadAdDataAndKeywords(
        currentService.ms_id,
        currentAction.ma_id,
        1
      );
    }
  }, [statusFilter, isUrgent, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // 모바일이면 모바일 컴포넌트 렌더링 (모든 hooks 호출 후)
  if (isMobile) {
    return <MyAdvertisementMobile />;
  }

  const statusOptions = [
    { value: "ALL", label: "전체" },
    { value: "APPROVED", label: "진행중" },
    { value: "PAUSED", label: "일시정지" },
    { value: "PENDING", label: "승인대기중" },
    { value: "REJECTED", label: "거절" },
    { value: "DONE", label: "종료" },
  ];

  // UTC 시간을 한국 시간으로 변환하는 함수
  const convertAdDatesToKoreaTime = (ads) => {
    return ads.map((ad) => {
      const convertedAd = { ...ad };

      // startDate 변환
      if (ad.startDate) {
        const koreaStartDate = convertUTCToKoreaTime(ad.startDate);
        if (koreaStartDate) {
          convertedAd.startDate = koreaStartDate.toISOString().split("T")[0];
        }
      }

      // endDate 변환
      if (ad.endDate) {
        const koreaEndDate = convertUTCToKoreaTime(ad.endDate);
        if (koreaEndDate) {
          convertedAd.endDate = koreaEndDate.toISOString().split("T")[0];
        }
      }

      // createdAt 변환 (필요한 경우)
      if (ad.createdAt) {
        const koreaCreatedAt = convertUTCToKoreaTime(ad.createdAt);
        if (koreaCreatedAt) {
          convertedAd.createdAt = koreaCreatedAt.toISOString();
        }
      }

      return convertedAd;
    });
  };

  const loadAdSummaryCounts = async () => {
    if (!user?.id) {
      return;
    }

    try {
      const memberId =
        user.role === "CHECKER" ? user.id : user.webMemberId || user.id;
      const baseOptions = {};
      if (user.role === "CHECKER") {
        baseOptions.checkerId = memberId;
      }

      const fetchCount = async (extra = {}) => {
        const response = await getMemberAdAPI(
          user.role === "CHECKER" ? null : memberId,
          1,
          1,
          { ...baseOptions, ...extra }
        );
        return Number(response.data?.pagination?.total) || 0;
      };

      const [total, active, pending, ended] = await Promise.all([
        fetchCount(),
        fetchCount({ status: "APPROVED" }),
        fetchCount({ status: "PENDING" }),
        fetchCount({ status: "DONE" }),
      ]);

      setAdSummary({ total, active, pending, ended });
    } catch (err) {
      console.error("광고 요약 통계 로딩 실패:", err);
    }
  };

  // 미션 서비스 목록 가져오기 (메인 탭)
  const loadMissionServices = async () => {
    if (!user?.id) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await getMissionServiceAPI();

      if (response.data?.data) {
        const services = response.data.data.map((service) => ({
          id: service.ms_id,
          name: service.name + " 광고",
          originalName: service.name,
          ms_id: service.ms_id,
        }));
        setMissionServices(services);

        // 첫 번째 서비스 선택 및 액션 목록 가져오기
        // 유튜브 광고 탭 비활성화 — 유튜브가 아닌 첫 서비스 선택
        if (services.length > 0) {
          const firstService =
            services.find(
              (service) => !String(service.name || "").includes("유튜브"),
            ) || services[0];

          setSelectedMainTab(firstService.id);
          await loadMissionActions(firstService.ms_id);
        }
      }
    } catch (err) {
      console.error("미션 서비스 로딩 실패:", err);
      setError("미션 서비스 목록을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 미션 액션 목록 가져오기 (서브 탭)
  const loadMissionActions = async (ms_id) => {
    if (!user?.id) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await getMissionActionAPI(ms_id);

      if (response.data?.data) {
        const actions = response.data.data.map((action) => ({
          id: action.ma_id,
          name: action.name,
          ma_id: action.ma_id,
        }));

        // 원하는 순서로 정렬: 쇼핑 방문(1) → 쇼핑(원부) 방문(2) → 플레이스 방문(3) → 플레이스 저장(4)
        const sortedActions = actions.sort((a, b) => {
          // ma_id 순서: 1, 2, 3, 4
          const order = [1, 2, 3, 4];
          return order.indexOf(a.ma_id) - order.indexOf(b.ma_id);
        });

        setMissionActions(sortedActions);

        // 첫 번째 액션 선택
        if (sortedActions.length > 0) {
          const firstAction = sortedActions[0];

          setSelectedSubTab(firstAction.id);

          // 첫 번째 액션 선택 후 광고 데이터와 키워드 데이터 로딩
          await loadAdDataAndKeywords(ms_id, firstAction.ma_id, 1);
        }
      }
    } catch (err) {
      console.error("미션 액션 로딩 실패:", err);
      setError("미션 액션 목록을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const buildKeywordDataByAdId = (keywords) => {
    const keywordMap = {};
    keywords.forEach((keyword) => {
      const adId = keyword.ad_id;
      if (!keywordMap[adId]) {
        keywordMap[adId] = [];
      }
      keywordMap[adId].push(keyword);
    });
    return keywordMap;
  };

  const fetchAllFilteredAdIds = async (memberId, apiParams) => {
    const requestMemberId = user.role === "CHECKER" ? null : memberId;
    const adLimit = 100;
    const firstPageResponse = await getMemberAdAPI(
      requestMemberId,
      1,
      adLimit,
      apiParams
    );

    const firstAds = firstPageResponse.data?.data || [];
    const firstPagination = firstPageResponse.data?.pagination || {};
    const totalFromPagination = Number(firstPagination.total) || firstAds.length;
    const totalPagesFromServer = Number(firstPagination.totalPages);
    const effectiveLimit = Number(firstPagination.limit) || adLimit;
    const totalPages = Number.isFinite(totalPagesFromServer) && totalPagesFromServer > 0
      ? totalPagesFromServer
      : Math.max(1, Math.ceil(totalFromPagination / effectiveLimit));

    const allAdIds = new Set(firstAds.map((ad) => ad.ad_id).filter(Boolean));

    for (let currentPage = 2; currentPage <= totalPages; currentPage += 1) {
      const pageResponse = await getMemberAdAPI(
        requestMemberId,
        currentPage,
        adLimit,
        apiParams
      );
      const pageAds = pageResponse.data?.data || [];
      pageAds.forEach((ad) => {
        if (ad.ad_id) {
          allAdIds.add(ad.ad_id);
        }
      });
    }

    return Array.from(allAdIds);
  };

  const fetchKeywordsByAdIds = async (adIds) => {
    if (!adIds.length) {
      return {};
    }

    const adIdChunkSize = 50;
    const keywordLimit = 100;
    const allKeywords = [];

    for (let i = 0; i < adIds.length; i += adIdChunkSize) {
      const adIdChunk = adIds.slice(i, i + adIdChunkSize);
      let currentPage = 1;
      let hasMore = true;

      while (hasMore) {
        const keywordResponse = await getKeywordNameAPI(
          null,
          adIdChunk,
          currentPage,
          keywordLimit
        );
        const chunkKeywords = keywordResponse.data?.data || [];
        const pagination = keywordResponse.data?.pagination || {};
        allKeywords.push(...chunkKeywords);

        const totalPagesFromServer = Number(pagination.totalPages);
        if (Number.isFinite(totalPagesFromServer) && totalPagesFromServer > 0) {
          hasMore = currentPage < totalPagesFromServer;
        } else {
          hasMore = chunkKeywords.length === keywordLimit;
        }
        currentPage += 1;
      }
    }

    return buildKeywordDataByAdId(allKeywords);
  };

  // 광고 데이터와 키워드 데이터 새로고침
  const refreshAdDataOnly = async (missionType, missionTemplate, page = 1) => {
    if (!user?.id) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // CHECKER 역할일 때는 checkerId 사용, 그 외에는 webMemberId 사용
      const memberId = user.role === "CHECKER" ? user.id : (user.webMemberId || user.id);
      const apiParams = {
        missionType: missionType,
        missionTemplate: missionTemplate,
        status: statusFilter !== "ALL" ? statusFilter : undefined,
        [searchType]: searchTerm || undefined,
        urgent: isUrgent ? true : undefined,
      };

      // CHECKER 역할일 때는 checkerId를 파라미터로 추가
      if (user.role === "CHECKER") {
        apiParams.checkerId = memberId;
      }

      // 광고 데이터 가져오기
      const adResponse = await getMemberAdAPI(
        user.role === "CHECKER" ? null : memberId, // CHECKER일 때는 첫 번째 파라미터를 null로
        page,
        AD_PAGE_SIZE,
        apiParams
      );

      if (adResponse.data?.data) {
        const ads = adResponse.data.data;
        const paginationInfo = adResponse.data.pagination;

        // UTC 시간을 한국 시간으로 변환
        const convertedAds = convertAdDatesToKoreaTime(ads);
        setAdData(convertedAds);
        setPagination(paginationInfo);

        // 페이징 상태 업데이트
        if (paginationInfo) {
          setCurrentPage(paginationInfo.page || 1);
          // 서버가 limit을 제대로 처리하지 않을 경우를 대비해 프론트엔드에서 계산
          const actualLimit = AD_PAGE_SIZE; // 실제 요청한 limit
          const total = paginationInfo.total || ads.length;
          const calculatedTotalPages = Math.ceil(total / actualLimit);

          setTotalPages(calculatedTotalPages);
          setTotalItems(total);
        }

        try {
          const allAdIds = await fetchAllFilteredAdIds(memberId, apiParams);
          const newKeywordData = await fetchKeywordsByAdIds(allAdIds);
          setKeywordData(newKeywordData);
        } catch (err) {
          console.error("키워드 데이터 로딩 실패:", err);
          setKeywordData({});
        }
      }
    } catch (err) {
      console.error("광고 데이터 로딩 실패:", err);
      setError("광고 목록을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 광고 데이터와 키워드 데이터 로딩
  const loadAdDataAndKeywords = async (
    missionType,
    missionTemplate,
    page = 1
  ) => {
    if (!user?.id) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // CHECKER 역할일 때는 checkerId 사용, 그 외에는 webMemberId 사용
      const memberId = user.role === "CHECKER" ? user.id : (user.webMemberId || user.id);
      const apiParams = {
        missionType: missionType, // 네이버 광고
        missionTemplate: missionTemplate,
        status: statusFilter !== "ALL" ? statusFilter : undefined,
        [searchType]: searchTerm || undefined,
        urgent: isUrgent ? true : undefined,
      };

      // CHECKER 역할일 때는 checkerId를 파라미터로 추가
      if (user.role === "CHECKER") {
        apiParams.checkerId = memberId;
      }

      // 1. 광고 데이터 가져오기
      const adResponse = await getMemberAdAPI(
        user.role === "CHECKER" ? null : memberId, // CHECKER일 때는 첫 번째 파라미터를 null로
        page,
        AD_PAGE_SIZE, // limit
        apiParams
      );

      if (adResponse.data?.data) {
        const ads = adResponse.data.data;
        const paginationInfo = adResponse.data.pagination;

        // UTC 시간을 한국 시간으로 변환
        const convertedAds = convertAdDatesToKoreaTime(ads);
        setAdData(convertedAds);
        setPagination(paginationInfo);

        // 페이징 상태 업데이트
        if (paginationInfo) {
          setCurrentPage(paginationInfo.page || 1);
          // 서버가 limit을 제대로 처리하지 않을 경우를 대비해 프론트엔드에서 계산
          const actualLimit = AD_PAGE_SIZE; // 실제 요청한 limit
          const total = paginationInfo.total || ads.length;
          const calculatedTotalPages = Math.ceil(total / actualLimit);

          setTotalPages(calculatedTotalPages);
          setTotalItems(total);
        }

        try {
          const allAdIds = await fetchAllFilteredAdIds(memberId, apiParams);
          const newKeywordData = await fetchKeywordsByAdIds(allAdIds);
          setKeywordData(newKeywordData);
        } catch (err) {
          console.error("❌ 키워드 데이터 로딩 실패:", err);
          setKeywordData({});
        }
      }

      // 3. 가격 등급 데이터 가져오기 (광고 연장용)
      if (user?.price_grade_id) {
        try {
          const priceResponse = await getPriceGradeDetailAPI(
            user.price_grade_id
          );
          setPriceGradeData(priceResponse.data?.data);
        } catch (err) {
          console.error("가격 등급 데이터 로딩 실패:", err);
        }
      }
    } catch (err) {
      console.error("광고 데이터 로딩 실패:", err);
      setError("광고 목록을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 메인 탭에 따라 서브 탭을 동적으로 반환
  const getSubTabs = () => {
    return missionActions.map((action) => ({
      id: action.id,
      label: action.name,
      active: selectedSubTab === action.id,
    }));
  };

  // 메인 탭 변경 핸들러
  const handleMainTabChange = async (tabId) => {
    setSelectedMainTab(tabId);
    setSelectedAds([]); // 선택 상태 초기화
    setCurrentPage(1); // 페이지를 1로 리셋

    // 로그인 상태일 때만 API 호출
    if (user?.id) {
      // 선택된 서비스의 ms_id 찾기
      const selectedService = missionServices.find(
        (service) => service.id === tabId
      );

      if (selectedService) {
        await loadMissionActions(selectedService.ms_id);
        // 서브 탭은 loadMissionActions에서 첫 번째 액션으로 자동 설정됨
      }
    } else {
      const availableSubTabs = mockSubTabs[tabId];

      if (availableSubTabs && availableSubTabs.length > 0) {
        const firstSubTab = availableSubTabs[0];

        setSelectedSubTab(firstSubTab.id);
      } else {
        console.warn(
          `🎭 로그아웃 상태, ${tabId} 탭에 대한 서브 탭이 없습니다.`
        );
      }
    }
  };

  // 서브 탭 변경 핸들러 추가
  const handleSubTabChange = async (subTabId) => {
    setSelectedSubTab(subTabId);
    setSelectedAds([]); // 서브 탭 변경 시에도 선택 상태 초기화
    setCurrentPage(1); // 페이지를 1로 리셋

    // 로그인 상태일 때만 API 호출
    if (user?.id) {
      // 선택된 액션의 ma_id 찾기
      const selectedAction = missionActions.find(
        (action) => action.id === subTabId
      );

      if (selectedAction) {
        // 현재 선택된 서비스의 ms_id 찾기
        const selectedService = missionServices.find(
          (service) => service.id === selectedMainTab
        );
        await loadAdDataAndKeywords(
          selectedService?.ms_id || 1,
          selectedAction.ma_id,
          1
        );
      }
    }
  };

  // ✅ 자식에게 전달할 함수들
  const handleSelectAll = (approvedAds) => (e) => {
    if (e?.target?.checked) {
      const approvedIds = approvedAds.map((ad) => ad.ad_id || ad.id);
      setSelectedAds(approvedIds);
    } else {
      setSelectedAds([]);
    }
  };

  const handleSelectAd = (adId) => {
    if (selectedAds.includes(adId)) {
      setSelectedAds(selectedAds.filter((id) => id !== adId));
    } else {
      setSelectedAds([...selectedAds, adId]);
    }
  };

  const handleViewAd = (ad) => {
    setSelectedAdData(ad);
  };

  const handleCloseDetail = () => {
    setSelectedAdData(null);
  };

  // 순위체크 모달 열기 핸들러
  const handleOpenRankCheckModal = (ad) => {
    setSelectedAdForRankCheck(ad);
    setIsRankCheckModalOpen(true);
  };

  // 순위체크 모달 닫기 핸들러
  const handleCloseRankCheckModal = () => {
    setIsRankCheckModalOpen(false);
    setSelectedAdForRankCheck(null);
  };

  const handleSearch = async () => {
    if (!user?.id || !selectedMainTab || !selectedSubTab) {
      return;
    }

    const currentService = missionServices.find(
      (s) => s.id === selectedMainTab
    ) || { ms_id: 1 };
    const currentAction = missionActions.find(
      (a) => a.id === selectedSubTab
    ) || { ma_id: 1 };

    await loadAdDataAndKeywords(
      currentService.ms_id,
      currentAction.ma_id,
      1 // 검색 시 첫 페이지로 이동
    );
  };

  // 페이지 변경 핸들러
  const handlePageChange = async (newPage) => {
    if (newPage < 1 || newPage > totalPages) {
      return;
    }

    setCurrentPage(newPage);
    setSelectedAds([]); // 페이지 변경 시 선택 상태 초기화

    // 현재 선택된 서비스와 액션의 ID 찾기
    const currentService = missionServices.find(
      (s) => s.id === selectedMainTab
    ) || { ms_id: 1 };
    const currentAction = missionActions.find(
      (a) => a.id === selectedSubTab
    ) || { ma_id: 1 };

    await loadAdDataAndKeywords(
      currentService.ms_id,
      currentAction.ma_id,
      newPage
    );
  };

  const handleBulkExtend = () => {
    if (selectedAds.length === 0) {
      alert("연장할 광고를 선택해주세요.");
      return;
    }

    setIsBulkExtensionModalOpen(true);
  };

  // 로그인되지 않은 경우 목데이터
  const mockMainTabs = [
    { id: "naver", label: "네이버 광고", active: selectedMainTab === "naver" },
    { id: "review", label: "리뷰 광고", active: selectedMainTab === "review" },
    // { id: "sns", label: "SNS", active: selectedMainTab === "sns" },
    // {
    //   id: "other",
    //   label: "기타광고",
    //   active: selectedMainTab === "other",
    // },
  ];

  // 로그아웃 시 보여줄 목데이터
  const logoImage = "/header_icon.ico";

  const mockAdData = {
    shopping: [
      {
        ad_id: 1234567891,
        category: "쇼핑 방문",
        missionName: "올려줌 S22 울트라 폰케이스",
        imageUrl: logoImage,
        storeName: "올려줌 공식 스토어",
        mainKeyword: "휴대폰 케이스",
        originalUrl: "https://smartstore.naver.com/olzoom",
        productUrl: "https://smartstore.naver.com/olzoom/products/123456",
        initialRank: 58,
        currentRank: 2,
        totalQuantity: "3,000",
        startDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        endDate: new Date(Date.now() + 9 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        status: "APPROVED",
        createdAt: "2025-08-10T01:18:43.053Z",
      },
      {
        ad_id: 1234567893,
        category: "쇼핑 방문",
        missionName: "올려줌 올레드 TV 65인치",
        imageUrl: logoImage,
        storeName: "올려줌 공식 스토어",
        mainKeyword: "TV",
        originalUrl: "https://smartstore.naver.com/olzoom",
        productUrl: "https://smartstore.naver.com/olzoom/products/345678",
        initialRank: 32,
        currentRank: 8,
        totalQuantity: "1,800",
        startDate: new Date(Date.now() - 11 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        endDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        status: "DONE",
        createdAt: "2025-08-08T01:18:43.053Z",
      },
    ],
    shopping_origin: [
      {
        ad_id: 1234567897,
        category: "쇼핑(원부) 방문",
        missionName: "올려줌 멀티비타민 30정",
        imageUrl: logoImage,
        storeName: "올려줌 공식 스토어",
        mainKeyword: "멀티비타민",
        originalUrl: "https://smartstore.naver.com/olzoom",
        productUrl: "https://smartstore.naver.com/olzoom/products/123456",
        originId: "https://smartstore.naver.com/olzoom/origin/123456",
        initialRank: 58,
        currentRank: 2,
        totalQuantity: "3,000",
        startDate: new Date(Date.now() - 11 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        endDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        status: "DONE",
        createdAt: "2025-08-10T01:18:43.053Z",
      },
      {
        ad_id: 1234567898,
        category: "쇼핑(원부) 방문",
        missionName: "올려줌 홍삼정 10ml x 30포",
        imageUrl: logoImage,
        storeName: "올려줌 공식 스토어",
        mainKeyword: "홍삼",
        originalUrl: "https://smartstore.naver.com/olzoom",
        productUrl: "https://smartstore.naver.com/olzoom/products/789012",
        originId: "https://smartstore.naver.com/olzoom/origin/789012",
        initialRank: 45,
        currentRank: 12,
        totalQuantity: "2,500",
        startDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        endDate: new Date(Date.now() + 9 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        status: "APPROVED",
        createdAt: "2025-08-12T01:18:43.053Z",
      },
    ],
    place: [
      {
        ad_id: 1234567903,
        category: "플레이스 방문",
        missionName: "올려줌 헤어 신촌점",
        imageUrl: logoImage,
        mainKeyword: "신촌 미용실",
        originalUrl: "https://map.naver.com/place/123456",
        productUrl: "https://map.naver.com/place/123456",
        initialRank: 58,
        currentRank: 2,
        totalQuantity: "3,000",
        startDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        endDate: new Date(Date.now() + 9 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        status: "APPROVED",
        createdAt: "2025-08-10T01:18:43.053Z",
      },
      {
        ad_id: 1234567904,
        category: "플레이스 방문",
        missionName: "올려줌 수제버거 강남역점",
        imageUrl: logoImage,
        mainKeyword: "강남 맛집",
        originalUrl: "https://map.naver.com/place/789012",
        productUrl: "https://map.naver.com/place/789012",
        initialRank: 45,
        currentRank: 12,
        totalQuantity: "2,500",
        startDate: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        endDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        status: "PENDING",
        createdAt: "2025-08-12T01:18:43.053Z",
      },
    ],
    save: [
      {
        ad_id: 1234567909,
        category: "플레이스 저장",
        missionName: "올려줌 카페 서울시청점",
        imageUrl: logoImage,
        mainKeyword: "서울 카페",
        originalUrl: "https://map.naver.com/place/123456",
        productUrl: "https://map.naver.com/place/123456",
        initialRank: 58,
        currentRank: 2,
        totalQuantity: "3,000",
        startDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        endDate: new Date(Date.now() + 9 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        status: "APPROVED",
        createdAt: "2025-08-10T01:18:43.053Z",
      },
      {
        ad_id: 1234567910,
        category: "플레이스 저장",
        missionName: "올려줌 방탈출카페 강남점",
        imageUrl: logoImage,
        mainKeyword: "강남 방탈출카페",
        originalUrl: "https://map.naver.com/place/789012",
        productUrl: "https://map.naver.com/place/789012",
        initialRank: 45,
        currentRank: 12,
        totalQuantity: "2,500",
        startDate: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        endDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        status: "PENDING",
        createdAt: "2025-08-12T01:18:43.053Z",
      },
    ],
    app: [
      {
        ad_id: 1234567915,
        category: "앱 리뷰",
        missionName: "올려줌 헬스앱",
        imageUrl: logoImage,
        storeName: "올려줌 개발사",
        mainKeyword: "헬스",
        originalUrl:
          "https://play.google.com/store/apps/details?id=com.olzoom.health",
        productUrl:
          "https://play.google.com/store/apps/details?id=com.olzoom.health",
        initialRank: 58,
        currentRank: 2,
        totalQuantity: "3,000",
        startDate: new Date(Date.now() - 11 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        endDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        status: "DONE",
        createdAt: "2025-08-10T01:18:43.053Z",
      },
      {
        ad_id: 1234567916,
        category: "앱 리뷰",
        missionName: "올려줌 교육앱",
        imageUrl: logoImage,
        storeName: "올려줌 개발사",
        mainKeyword: "교육",
        originalUrl:
          "https://play.google.com/store/apps/details?id=com.olzoom.education",
        productUrl:
          "https://play.google.com/store/apps/details?id=com.olzoom.education",
        initialRank: 45,
        currentRank: 12,
        totalQuantity: "2,500",
        startDate: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        endDate: new Date(Date.now() + 11 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        status: "PENDING",
        createdAt: "2025-08-12T01:18:43.053Z",
      },
    ],
  };

  // 실제 데이터 또는 목데이터 사용
  // 유튜브 광고 탭 비활성화 (안 보이게)
  const mainTabs = user?.id
    ? missionServices
        .filter((service) => !String(service.name || "").includes("유튜브"))
        .map((service) => ({
          id: service.id,
          label: service.name,
          active: selectedMainTab === service.id,
        }))
    : mockMainTabs;

  // 서브탭도 로그인 상태에 따라 분기
  const getSubTabsForRender = () => {
    if (!user?.id) {
      return mockSubTabs[selectedMainTab] || [];
    }
    return getSubTabs();
  };

  // 현재 선택된 광고 타입에 따라 컴포넌트 렌더링
  const renderAdContent = () => {
    const commonProps = {
      selectedAds,
      statusFilter,
      searchTerm,
      onSelectAll: handleSelectAll, // 함수 자체를 전달
      onSelectAd: handleSelectAd,
      onViewAd: handleViewAd,
      onOpenRankCheck: handleOpenRankCheckModal, // 순위체크 모달 열기 함수 추가
      getStatusClass,
      getStatusText,
    };

    // 로그아웃 상태일 때는 목데이터 사용
    if (!user?.id) {
      const mockData = mockAdData[selectedSubTab] || [];

      if (selectedMainTab === "naver") {
        if (selectedSubTab === "shopping") {
          return (
            <ShoppingAdTable
              key={`shopping-${Date.now()}`}
              {...commonProps}
              adData={mockData}
              keywordData={{}}
              priceGradeData={null}
            />
          );
        } else if (selectedSubTab === "shopping_origin") {
          return (
            <ShoppingOriginAdTable
              key={`shopping_origin-${Date.now()}`}
              {...commonProps}
              adData={mockData}
              keywordData={{}}
            />
          );
        } else if (selectedSubTab === "place") {
          return (
            <PlaceAdTable
              key={`place-${Date.now()}`}
              {...commonProps}
              adData={mockData}
              keywordData={{}}
            />
          );
        } else if (selectedSubTab === "save") {
          return (
            <PlaceAdTable
              key={`save-${Date.now()}`}
              {...commonProps}
              adData={mockData}
              keywordData={{}}
            />
          );
        } else {
          return (
            <ShoppingAdTable
              key={`shopping-default-${Date.now()}`}
              {...commonProps}
              adData={mockData}
              keywordData={{}}
              priceGradeData={null}
            />
          );
        }
      } else if (selectedMainTab === "review") {
        return (
          <ReviewAdTable
            key={`review-${Date.now()}`}
            {...commonProps}
            adData={mockData}
            keywordData={{}}
          />
        );
      } else {
        return (
          <ShoppingAdTable
            key={`shopping-fallback-${Date.now()}`}
            {...commonProps}
            adData={mockData}
            keywordData={{}}
          />
        );
      }
    }

    // 로그인 상태일 때만 API 관련 로직 실행
    // 네이버 광고 탭 확인 (API에서 온 숫자 ID 또는 문자열 "naver")
    const isNaverTab = selectedMainTab === "naver" || selectedMainTab === 1;
    // 리뷰 광고 탭 확인 (API에서 온 숫자 ID 또는 문자열 "review")
    const isReviewTab = selectedMainTab === "review" || selectedMainTab === 2;
    // 쿠팡 서비스 탭 확인 (missionType: 4 또는 selectedSubTab: 13)
    const isCoupangTab = selectedMainTab === 4 || selectedSubTab === 13;

    if (isNaverTab) {
      // 현재 선택된 서비스와 액션의 ID 찾기
      const currentService = missionServices.find(
        (s) => s.id === selectedMainTab
      ) || { ms_id: 1 }; // 기본값 (네이버)
      const currentAction = missionActions.find(
        (a) => a.id === selectedSubTab
      ) || { ma_id: 1 }; // 기본값 (쇼핑 방문)

      const enhancedProps = {
        ...commonProps,
        adData: adData, // 광고 데이터 전달
        keywordData: keywordData, // 키워드 데이터 전달
        priceGradeData: priceGradeData, // 가격 등급 데이터 전달
      };

      // 서브탭도 숫자 또는 문자열 처리
      if (selectedSubTab === "shopping" || selectedSubTab === 1) {
        return (
          <ShoppingAdTable
            key="shopping"
            {...enhancedProps}
            keywordData={keywordData}
          />
        );
      } else if (selectedSubTab === "shopping_origin" || selectedSubTab === 2) {
        return (
          <ShoppingOriginAdTable
            key="shopping_origin"
            {...enhancedProps}
            keywordData={keywordData}
          />
        );
      } else if (selectedSubTab === "place" || selectedSubTab === 3) {
        return (
          <PlaceAdTable
            key="place"
            {...enhancedProps}
            keywordData={keywordData}
          />
        );
      } else if (selectedSubTab === "save" || selectedSubTab === 4) {
        return (
          <PlaceAdTable
            key="save"
            {...enhancedProps}
            keywordData={keywordData}
          />
        );
      } else if (selectedSubTab === 13) {
        // 쿠팡 방문 (missionTemplate: 13)
        return (
          <ShoppingAdTable
            key="coupang"
            {...enhancedProps}
            keywordData={keywordData}
          />
        );
      } else {
        return (
          <ShoppingAdTable
            key="shopping"
            {...enhancedProps}
            keywordData={keywordData}
          />
        );
      }
    } else if (isReviewTab) {
      // 현재 선택된 서비스와 액션의 ID 찾기
      const currentService = missionServices.find(
        (s) => s.id === selectedMainTab
      ) || { ms_id: 2 }; // 기본값 (리뷰)
      const currentAction = missionActions.find(
        (a) => a.id === selectedSubTab
      ) || { ma_id: 5 }; // 기본값 (앱 리뷰)

      const enhancedProps = {
        ...commonProps,
        adData: adData, // 광고 데이터 전달
        keywordData: keywordData, // 키워드 데이터 전달
        priceGradeData: priceGradeData, // 가격 등급 데이터 전달
      };

      return (
        <ReviewAdTable
          key="review"
          {...enhancedProps}
          keywordData={keywordData}
        />
      );
    } else if (isCoupangTab || selectedSubTab === 13) {
      // 쿠팡 서비스 처리
      const currentService = missionServices.find(
        (s) => s.id === selectedMainTab
      ) || { ms_id: 4 }; // 기본값 (쿠팡)
      const currentAction = missionActions.find(
        (a) => a.id === selectedSubTab
      ) || { ma_id: 13 }; // 기본값 (쿠팡 방문)

      const enhancedProps = {
        ...commonProps,
        adData: adData,
        keywordData: keywordData,
        priceGradeData: priceGradeData,
      };

      return (
        <ShoppingAdTable
          key="coupang"
          {...enhancedProps}
          keywordData={keywordData}
        />
      );
    } else {
      return (
        <div className="no-data-message">
          <p>등록된 광고가 없습니다</p>
        </div>
      );
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "APPROVED":
        return "status-progress";
      case "PENDING":
        return "status-pending";
      case "PAUSED":
        return "status-paused";
      case "REJECTED":
        return "status-rejected"; // ✅ 반려 상태 추가
      case "REFUND":
        return "status-refund";
      case "DONE":
        return "status-ended";
      default:
        return "";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "APPROVED":
        return "진행중";
      case "PAUSED":
        return "일시정지";
      case "PENDING":
        return "승인대기중";
      case "REJECTED":
        return "반려"; // ✅ 반려 상태 텍스트 추가
      case "REFUND":
        return "환불";
      case "DONE":
        return "종료";
      default:
        return status;
    }
  };

  return (
    <div className="MyaddAdvertisement-widget-card">
      {/* 요약 통계 — 상세보기 모드가 아닐 때만 */}
      {!selectedAdData && (
        <div className="Myadd-summary Myadd-summary-v2" aria-label="광고 현황 요약">
          <div className="Myadd-summary-tile is-total">
            <span className="Myadd-summary-tile-label">내 전체 광고수</span>
            <strong className="Myadd-summary-tile-value">
              {Number(adSummary.total).toLocaleString()}
              <span className="Myadd-summary-tile-unit">건</span>
            </strong>
          </div>
          <div className="Myadd-summary-tile is-active">
            <span className="Myadd-summary-tile-label">진행중</span>
            <strong className="Myadd-summary-tile-value">
              {Number(adSummary.active).toLocaleString()}
              <span className="Myadd-summary-tile-unit">건</span>
            </strong>
          </div>
          <div className="Myadd-summary-tile is-pending">
            <span className="Myadd-summary-tile-label">승인대기중</span>
            <strong className="Myadd-summary-tile-value">
              {Number(adSummary.pending).toLocaleString()}
              <span className="Myadd-summary-tile-unit">건</span>
            </strong>
          </div>
          <div className="Myadd-summary-tile is-ended">
            <span className="Myadd-summary-tile-label">종료</span>
            <strong className="Myadd-summary-tile-value">
              {Number(adSummary.ended).toLocaleString()}
              <span className="Myadd-summary-tile-unit">건</span>
            </strong>
          </div>
        </div>
      )}

      {/* 탭 컨테이너 - 메인 탭과 서브 탭을 같은 줄에 배치 */}
      {!selectedAdData && (
        <div className="Myadd-tabs-container">
          {/* 메인 탭 */}
          <div className="Myadd-main-tabs">
            {mainTabs.map((tab) => (
              <button
                key={tab.id}
                className={`Myadd-main-tab ${tab.active ? "active" : ""}`}
                onClick={() =>
                  user?.id
                    ? handleMainTabChange(tab.id)
                    : setSelectedMainTab(tab.id)
                }
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* 서브 탭이 있을 때만 divider와 서브 탭 표시 */}
          {getSubTabsForRender().length > 0 && (
            <>
              <div className="Myadd-tabs-divider"></div>
              <div className="Myadd-sub-tabs-left">
                {getSubTabsForRender().map((tab) => (
                  <button
                    key={tab.id}
                    className={`Myadd-sub-tab ${tab.active ? "active" : ""}`}
                    onClick={() =>
                      user?.id
                        ? handleSubTabChange(tab.id)
                        : setSelectedSubTab(tab.id)
                    }
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {/* 서브 탭 - 상세보기 모드가 아닐 때만 표시 */}
      {!selectedAdData && getSubTabsForRender().length > 0 && (
        <div className="Myadd-sub-tabs">
          {/* 검색 및 액션 */}
          <div className="search-action">
            <div className="status-filter">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="status-select"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="urgent-filter">
              <label className="urgent-checkbox-label">
                <input
                  type="checkbox"
                  checked={isUrgent}
                  onChange={(e) => setIsUrgent(e.target.checked)}
                  className="urgent-checkbox"
                />
                <span className="urgent-text">마감임박</span>
              </label>
            </div>
            <div className="search-action-spacer" />
            <div className="search-action-right">
              <div className="search-field">
                <select
                  className="search-field-type"
                  value={searchType}
                  onChange={(e) => setSearchType(e.target.value)}
                  aria-label="검색 유형"
                >
                  <option value="missionName">상품명</option>
                  <option value="storeName">스토어 이름</option>
                </select>
                <input
                  className="search-field-input"
                  type="text"
                  placeholder={
                    searchType === "missionName"
                      ? "상품명으로 찾기"
                      : "스토어 이름으로 찾기"
                  }
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                />
                <button
                  type="button"
                  className="search-field-submit"
                  onClick={handleSearch}
                >
                  검색
                </button>
              </div>
              <button
                className="bulk-extend-btn"
                onClick={handleBulkExtend}
                disabled={!user?.id}
                style={{
                  opacity: user?.id ? 1 : 0.5,
                  cursor: user?.id ? "pointer" : "not-allowed",
                }}
              >
                광고 일괄 연장
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 광고 내용 렌더링 */}
      {loading ? (
        <div className="loading-message">광고 데이터를 불러오는 중...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : selectedAdData ? (
        // 상세보기 모드
        <div className="ad-detail-view-container">
          <div className="ad-detail-view-header">
            <div className="ad-detail-view-header-left">
              <h3 className="ad-detail-view-title">광고 상세 정보</h3>
              {missionActions.find((a) => a.id === selectedSubTab)?.name && (
                <span className="ad-detail-page-sub">
                  {missionActions.find((a) => a.id === selectedSubTab)?.name}
                </span>
              )}
            </div>
            <button onClick={handleCloseDetail} className="back-to-list-btn">
              ← 목록으로 돌아가기
            </button>
          </div>
          <div className="ad-detail-view-content">
            {/* 쿠팡 방문 (missionTemplate: 13) - 쿠팡 전용 상세 */}
            {(selectedSubTab === 13 ||
              selectedAdData?.missionTemplate === 13) && (
              <CoupangAdDetail
                isOpen={true}
                onClose={handleCloseDetail}
                adData={selectedAdData}
                keywordData={keywordData[selectedAdData?.ad_id] || []}
                priceGradeData={priceGradeData}
                getUserDetailAPI={getUserDetailAPI}
                onSave={async () => {
                  const currentService = missionServices.find(
                    (s) => s.id === selectedMainTab
                  ) || { ms_id: 1 };
                  const currentAction = missionActions.find(
                    (a) => a.id === selectedSubTab
                  ) || { ma_id: 1 };

                  await refreshAdDataOnly(
                    currentService.ms_id,
                    currentAction.ma_id,
                    currentPage
                  );

                  const updatedAd = adData.find(
                    (ad) => ad.ad_id === selectedAdData.ad_id
                  );
                  if (updatedAd) {
                    setSelectedAdData(updatedAd);
                  }
                }}
              />
            )}

            {/* 쇼핑 관련 광고 (쇼핑 방문, 쇼핑 원부 방문) */}
            {(selectedSubTab === "shopping" ||
              selectedSubTab === 1 ||
              selectedSubTab === 2 ||
              selectedSubTab === "shopping_origin") && (
              <ShoppingAdDetail
                isOpen={true}
                onClose={handleCloseDetail}
                adData={selectedAdData}
                keywordData={keywordData[selectedAdData?.ad_id] || []}
                priceGradeData={priceGradeData}
                getUserDetailAPI={getUserDetailAPI}
                onSave={async () => {
                  const currentService = missionServices.find(
                    (s) => s.id === selectedMainTab
                  ) || { ms_id: 1 };
                  const currentAction = missionActions.find(
                    (a) => a.id === selectedSubTab
                  ) || { ma_id: 1 };

                  await refreshAdDataOnly(
                    currentService.ms_id,
                    currentAction.ma_id,
                    currentPage
                  );

                  // 상세보기 데이터도 새로고침
                  const updatedAd = adData.find(
                    (ad) => ad.ad_id === selectedAdData.ad_id
                  );
                  if (updatedAd) {
                    setSelectedAdData(updatedAd);
                  }
                }}
              />
            )}

            {/* 플레이스 관련 광고 (플레이스 방문, 플레이스 저장) */}
            {(selectedSubTab === "place" ||
              selectedSubTab === 3 ||
              selectedSubTab === "save" ||
              selectedSubTab === 4) && (
              <PlaceAdDetail
                isOpen={true}
                onClose={handleCloseDetail}
                adData={selectedAdData}
                keywordData={keywordData[selectedAdData?.ad_id] || []}
                priceGradeData={priceGradeData}
                getUserDetailAPI={getUserDetailAPI}
                onSave={async () => {
                  const currentService = missionServices.find(
                    (s) => s.id === selectedMainTab
                  ) || { ms_id: 1 };
                  const currentAction = missionActions.find(
                    (a) => a.id === selectedSubTab
                  ) || { ma_id: 1 };

                  await refreshAdDataOnly(
                    currentService.ms_id,
                    currentAction.ma_id,
                    currentPage
                  );

                  // 상세보기 데이터도 새로고침
                  const updatedAd = adData.find(
                    (ad) => ad.ad_id === selectedAdData.ad_id
                  );
                  if (updatedAd) {
                    setSelectedAdData(updatedAd);
                  }
                }}
              />
            )}

            {/* 앱 리뷰 광고 */}
            {(selectedSubTab === "app" ||
              selectedSubTab === 5 ||
              selectedSubTab === 6) && (
              <AppReviewAdDetail
                isOpen={true}
                onClose={handleCloseDetail}
                adData={selectedAdData}
                keywordData={keywordData[selectedAdData?.ad_id] || []}
                priceGradeData={priceGradeData}
                getUserDetailAPI={getUserDetailAPI}
                onSave={async () => {
                  const currentService = missionServices.find(
                    (s) => s.id === selectedMainTab
                  ) || { ms_id: 1 };
                  const currentAction = missionActions.find(
                    (a) => a.id === selectedSubTab
                  ) || { ma_id: 1 };

                  await refreshAdDataOnly(
                    currentService.ms_id,
                    currentAction.ma_id,
                    currentPage
                  );

                  // 상세보기 데이터도 새로고침
                  const updatedAd = adData.find(
                    (ad) => ad.ad_id === selectedAdData.ad_id
                  );
                  if (updatedAd) {
                    setSelectedAdData(updatedAd);
                  }
                }}
              />
            )}
          </div>
        </div>
      ) : (
        // 목록 모드
        <div className="Myadd-table-scroll">{renderAdContent()}</div>
      )}

      {/* 페이징 + 내역 버튼 — 상세보기 모드가 아닐 때 */}
      {!selectedAdData && (
        <div className="pagination-container Myadd-pagination-bar">
          <div className="Myadd-pagination-left">
            <div className="Myadd-footer-actions">
              <button
                type="button"
                className="Myadd-quantity-history-btn Myadd-history-btn-secondary"
                onClick={() => setIsQuantityIncreaseHistoryOpen(true)}
                disabled={!user?.id}
              >
                수량증가 내역
              </button>
              <button
                type="button"
                className="Myadd-quantity-history-btn Myadd-history-btn-secondary"
                onClick={() => setIsAdEndHistoryOpen(true)}
                disabled={!user?.id}
              >
                광고 종료 신청 내역
              </button>
            </div>
            {user?.id && (
              <div className="pagination-info">
                <span>총 {totalItems}건</span>
              </div>
            )}
          </div>
          {user?.id && (totalPages > 1 || totalItems > AD_PAGE_SIZE) && (
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                &lt;
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    className={`pagination-btn ${
                      currentPage === pageNum ? "active" : ""
                    }`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                &gt;
              </button>
            </div>
          )}
        </div>
      )}

      {/* 광고 일괄 연장 모달 */}
      {isBulkExtensionModalOpen && (
        <AdExtensionModal
          isOpen={isBulkExtensionModalOpen}
          onClose={() => setIsBulkExtensionModalOpen(false)}
          selectedAds={selectedAds
            .map((adId) => {
              const foundAd = adData.find((ad) => ad.ad_id === adId);
              if (!foundAd) {
                console.warn(
                  `광고 ID ${adId}에 해당하는 데이터를 찾을 수 없습니다.`
                );
                return null;
              }
              return foundAd;
            })
            .filter(Boolean)}
          onExtend={(result) => {
            if (result === "success") {
              // 연장 성공 시 광고 데이터 새로고침
              const currentService = missionServices.find(
                (s) => s.id === selectedMainTab
              ) || { ms_id: 1 };
              const currentAction = missionActions.find(
                (a) => a.id === selectedSubTab
              ) || { ma_id: 1 };

              loadAdDataAndKeywords(
                currentService.ms_id,
                currentAction.ma_id,
                currentPage
              );
              loadAdSummaryCounts();

              // 선택 상태 초기화
              setSelectedAds([]);

              // 성공 메시지 표시
              alert("광고 연장이 성공적으로 완료되었습니다!");
            }

            setIsBulkExtensionModalOpen(false);
          }}
        />
      )}

      {/* 순위체크 모달 */}
      <RankCheckModal
        isOpen={isRankCheckModalOpen}
        onClose={handleCloseRankCheckModal}
        adData={selectedAdForRankCheck}
      />

      {/* 수량증가 내역 모달 */}
      <QuantityIncreaseHistoryModal
        isOpen={isQuantityIncreaseHistoryOpen}
        onClose={() => setIsQuantityIncreaseHistoryOpen(false)}
        webMemberId={user?.id}
      />

      {/* 광고 종료 신청 내역 모달 */}
      <AdEndHistoryModal
        isOpen={isAdEndHistoryOpen}
        onClose={() => setIsAdEndHistoryOpen(false)}
        webMemberId={user?.webMemberId ?? user?.id}
      />
    </div>
  );
};

export default MyAdvertisement;
